import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { WorkExperienceCompany } from '../../../models/workExperienceCompany';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent, ColumnConfig } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Country } from '../../../models/country';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-work-experience-company-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './work-experience-company-list.component.html',
  styleUrl: './work-experience-company-list.component.scss'
})
export class WorkExperienceCompanyListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  workExperienceCompanies = signal<WorkExperienceCompany[]>([]);
  countries: Country[] = [];
  isSearchVisible: boolean = false;
  isExporting: boolean = false;
  matchedItems: WorkExperienceCompany[] | undefined = undefined;
  rightItem: WorkExperienceCompany | undefined = undefined;

  // Pagination state
  currentPage: number = 1;
  pageSize: number = 25;
  totalCount: number = 0;
  sortBy: string = 'name';
  ascending: boolean = true;

  // Search filter state
  searchFilter: LookupTableSearchFilter = {};

  workExperienceCompaniesColumns: ColumnConfig[] = [];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private lutService: LutService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCountries();
    this.loadWorkExperienceCompanies();
  }

  private loadCountries(): void {
    this.lutService.getCountries().subscribe({
      next: (countriesFlat) => {
        // Convert flat API response to hierarchical structure
        this.countries = Helpers.convertCountryFlatToCountry(countriesFlat).sort((a, b) => a.name.localeCompare(b.name));

        // Create select options for countries
        const countryOptions = this.countries.map(country => ({
          value: country.id,
          label: country.name
        }));

        this.workExperienceCompaniesColumns = [
          { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
          {
            key: 'countryId',
            header: 'Country',
            type: 'select',
            sortable: true,
            editable: true,
            selectOptions: countryOptions
          }
        ];
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.toast.error(`<strong>Failed to load Countries:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  loadWorkExperienceCompanies(): void {
    this.spinnerService.show();

    // Check if any search filters are active
    const hasSearchFilters = this.searchFilter.name || (this.searchFilter as any).countryId;

    if (hasSearchFilters) {
      // Use search endpoint when filters are active
      const searchRequest = {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        ascending: this.ascending,
        ...this.searchFilter
      };

      this.lutService.searchWorkExperienceCompanies(searchRequest).subscribe({
        next: (response) => {
          this.workExperienceCompanies.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading work experience companies:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Work Experience Companies:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    } else {
      // Use regular paginated endpoint when no filters are active
      this.lutService.getWorkExperienceCompaniesPaginated(
        this.currentPage,
        this.pageSize,
        this.sortBy,
        this.ascending
      ).subscribe({
        next: (response) => {
          this.workExperienceCompanies.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading work experience companies:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Work Experience Companies:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1; // MatPaginator uses 0-based index
    this.pageSize = event.pageSize;
    this.loadWorkExperienceCompanies();
  }

  onSortChange(sort: Sort): void {
    // Map column keys to actual sortable fields
    const sortColumnMap: { [key: string]: string } = {
      'countryId': 'countryName'
    };

    // If sort direction is cleared (empty string), reset to default sort
    if (!sort.direction) {
      this.sortBy = 'name';
      this.ascending = true;
    } else {
      this.sortBy = sortColumnMap[sort.active] || sort.active;
      this.ascending = sort.direction === 'asc';
    }

    this.loadWorkExperienceCompanies();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onExportToCsv(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.spinnerService.show();

    this.lutService.exportWorkExperienceCompaniesToCsv({
      name: this.searchFilter.name,
      countryId: (this.searchFilter as any).countryId
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `work_experience_companies_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.isExporting = false;
        this.toast.success('Work experience companies exported successfully');
      },
      error: (error) => {
        console.error('Error exporting work experience companies:', error);
        this.toast.error(`<strong>Failed to export Work Experience Companies:</strong></br></br> ${Helpers.parseApiError(error)}`);
        this.spinnerService.hide();
        this.isExporting = false;
      }
    });
  }

  getAppliedFiltersCount(): number {
    const { exactMatch, ...actualFilters } = this.searchFilter;
    return Object.values(actualFilters).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.currentPage = 1; // Reset to first page when searching

    // Reset the paginator to first page visually
    if (this.lookupTable?.['paginator']) {
      this.lookupTable['paginator'].pageIndex = 0;
    }

    this.loadWorkExperienceCompanies();
  }

  onCreateWorkExperienceCompany(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(workExperienceCompany: WorkExperienceCompany): void {
  }

  onReadyToMatch(event: {matchedItems?: WorkExperienceCompany[], rightItem?: WorkExperienceCompany}): void {
    this.matchedItems = event.matchedItems;
    this.rightItem = event.rightItem;
  }

  onWorkExperienceCompanyCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalCompanies = this.workExperienceCompanies();

        this.workExperienceCompanies.update(companies =>
          companies.filter(c => !bulkMergeRequest.leftIds.includes(c.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeWorkExperienceCompaniesBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Work experience companies merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadWorkExperienceCompanies();
          },
          error: (error) => {
            console.error('Error merging work experience companies:', error);
            this.spinnerService.hide();
            this.workExperienceCompanies.set(originalCompanies);
            this.rightItem = originalCompanies.find(c => c.id === bulkMergeRequest.rightId);
            this.matchedItems = originalCompanies.filter(c => bulkMergeRequest.leftIds.includes(c.id));
            this.toast.error(`<strong>Failed to merge Work Experience Companies:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onEditWorkExperienceCompany(workExperienceCompany: WorkExperienceCompany): void {
  }

  onDeleteWorkExperienceCompany(workExperienceCompany: WorkExperienceCompany): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(workExperienceCompany.name)
      .subscribe(result => {
        if (result) {
          const originalCompanies = this.workExperienceCompanies();

          this.workExperienceCompanies.update(companies => companies.filter(c => c.id !== workExperienceCompany.id));

          this.spinnerService.show();
          this.lutService.deleteWorkExperienceCompany(workExperienceCompany.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Work experience company deleted successfully');
              // Reload current page
              this.loadWorkExperienceCompanies();
            },
            error: (error) => {
              console.error('Error deleting work experience company:', error);
              this.spinnerService.hide();
              this.workExperienceCompanies.set(originalCompanies);
              this.toast.error(`<strong>Failed to delete Work Experience Company:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: WorkExperienceCompany, newName: string} | {item: WorkExperienceCompany, newValue: any, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: WorkExperienceCompany;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };

      // If updating countryId, also update countryName
      if (event.columnKey === 'countryId') {
        const countryName = event.newValue ? this.countries.find(c => c.id === event.newValue)?.name || null : null;
        updatedItem = { ...updatedItem, countryName: countryName };
      }
    }

    this.workExperienceCompanies.update(companies =>
      companies.map(c => c.id === event.item.id ? updatedItem : c)
    );

    this.spinnerService.show();
    this.lutService.updateWorkExperienceCompany(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Work experience company updated successfully');
        this.workExperienceCompanies.update(companies =>
          companies.map(c => c.id === event.item.id ? response : c)
        );
      },
      error: (error) => {
        console.error('Error updating work experience company:', error);
        this.spinnerService.hide();
        this.workExperienceCompanies.update(companies =>
          companies.map(c => c.id === event.item.id ? originalItem : c)
        );
        this.toast.error(`<strong>Failed to update Work Experience Company:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const countryId = event['countryId'] || null;
    const countryName = countryId ? this.countries.find(c => c.id === countryId)?.name || null : null;

    const newCompany: WorkExperienceCompany = {
      id: tempId,
      name: event['name'],
      countryId: countryId,
      countryName: countryName,
      systemReserved: false
    };

    this.workExperienceCompanies.update(companies => [newCompany, ...companies]);

    this.spinnerService.show();
    this.lutService.createWorkExperienceCompany({
      name: event['name'],
      countryId: countryId,
      countryName: countryName,
      systemReserved: false
    }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Work experience company created successfully');
        this.workExperienceCompanies.update(companies =>
          companies.map(c => c.id === tempId ? response : c)
        );
      },
      error: (error) => {
        console.error('Error creating work experience company:', error);
        this.spinnerService.hide();
        this.workExperienceCompanies.update(companies =>
          companies.filter(c => c.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Work Experience Company:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
